import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, LogOut, Moon, Search, Settings, Sun, User } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Brand, StatusBadge } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useFleet } from "@/context/FleetContext";
import { api } from "@/lib/api";
import { PRIORITY, timeAgo, vehicleStatus } from "@/lib/fleet";
import { cn } from "@/lib/utils";
import { InstallButton } from "@/components/MobileApp";

function VehicleSearch() {
  const { vehicles } = useFleet();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [focus, setFocus] = useState(false);
  const s = q.toLowerCase();
  const res = s ? vehicles.filter((v) => v.name.toLowerCase().includes(s) || v.plate.toLowerCase().includes(s) || (v.imei || "").includes(s)).slice(0, 7) : [];
  return (
    <div className="relative w-full max-w-md">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => setFocus(true)} onBlur={() => setTimeout(() => setFocus(false), 150)}
        placeholder="Cari kendaraan, plat, IMEI..." className="gt-input h-10 rounded-full pl-9" data-testid="topbar-search-input" />
      {focus && res.length > 0 && (
        <div className="absolute left-0 right-0 top-12 z-50 overflow-hidden rounded-2xl border border-border bg-popover p-1.5 shadow-2xl" data-testid="topbar-search-results">
          {res.map((v) => (
            <button key={v.id} onMouseDown={() => { nav(`/vehicles/${v.id}`); setQ(""); }} data-testid={`search-result-${v.id}`}
              className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left hover:bg-accent">
              <span><span className="text-sm font-semibold">{v.name}</span> <span className="ml-1 font-mono text-xs text-muted-foreground">{v.plate}</span></span>
              <StatusBadge status={vehicleStatus(v)} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NotificationBell() {
  const { unread, setUnread } = useFleet();
  const [items, setItems] = useState([]);
  const load = async (o) => {
    if (!o) return;
    const { data } = await api.get("/notifications", { params: { limit: 8 } });
    setItems(data);
  };
  const readAll = async () => {
    await api.post("/notifications/read-all");
    setUnread(0);
    setItems((p) => p.map((n) => ({ ...n, read: true })));
  };
  return (
    <Popover onOpenChange={load}>
      <PopoverTrigger asChild>
        <button className="relative flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card transition-colors hover:border-primary/50" data-testid="notification-bell">
          <Bell className="h-[18px] w-[18px]" />
          {unread > 0 && <span className="absolute -right-1 -top-1 min-w-[18px] rounded-full bg-red-500 px-1 text-center font-mono text-[10px] font-bold leading-[18px] text-white" data-testid="notification-unread-count">{unread > 99 ? "99+" : unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="z-[1000] w-[360px] border-border bg-popover p-0" data-testid="notification-popover">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <span className="text-sm font-bold">Notifikasi</span>
          <button onClick={readAll} className="text-xs font-semibold gt-brand-text hover:underline" data-testid="notification-read-all">Tandai semua dibaca</button>
        </div>
        <div className="gt-scroll max-h-[380px] overflow-y-auto p-1.5">
          {items.map((n) => (
            <div key={n.id} className="flex gap-3 rounded-xl px-3 py-2.5 hover:bg-accent">
              <span className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", n.read ? "bg-transparent" : PRIORITY[n.priority]?.dot)} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-semibold">{n.title}{n.vehicle_name ? ` · ${n.vehicle_name}` : ""}</div>
                <div className="truncate text-xs text-muted-foreground">{n.message}</div>
              </div>
              <span className="shrink-0 font-mono text-[10px] text-muted-foreground">{timeAgo(n.created_at)}</span>
            </div>
          ))}
          {!items.length && <div className="p-6 text-center text-xs text-muted-foreground">Belum ada notifikasi</div>}
        </div>
        <Link to="/notifications" className="block border-t border-border py-2.5 text-center text-xs font-bold hover:bg-accent" data-testid="notification-view-all">Buka Notification Center</Link>
      </PopoverContent>
    </Popover>
  );
}

export function Topbar() {
  const { user, logout, isDemo } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  return (
    <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-border bg-background/80 px-4 backdrop-blur-xl sm:px-6 lg:px-8" data-testid="topbar">
      <Brand compact className="lg:hidden" />
      <div className="hidden flex-1 sm:block"><VehicleSearch /></div>
      <div className="flex-1 sm:hidden" />
      {isDemo && (
        <span
          className="rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-[10px] font-extrabold tracking-wide text-amber-600"
          data-testid="demo-mode-indicator"
        >
          MODE DEMO
        </span>
      )}
      <InstallButton />
      <button onClick={toggle} className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card transition-colors hover:border-primary/50" data-testid="theme-toggle">
        {theme === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
      </button>
      <NotificationBell />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2.5 rounded-full border border-border bg-card py-1 pl-1 pr-3 transition-colors hover:border-primary/50" data-testid="profile-menu-trigger">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-extrabold text-primary-foreground">{user?.name?.[0] || "U"}</span>
            <span className="hidden text-left leading-tight md:block">
              <span className="block text-[13px] font-semibold">{user?.name}</span>
              <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">{user?.role === "admin" ? "Administrator" : user?.customer?.name}</span>
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 border-border bg-popover">
          <DropdownMenuLabel className="text-xs text-muted-foreground">{user?.email}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => nav("/users")} data-testid="profile-menu-account"><User className="mr-2 h-4 w-4" /> Akun</DropdownMenuItem>
          <DropdownMenuItem onClick={() => nav("/settings")} data-testid="profile-menu-settings"><Settings className="mr-2 h-4 w-4" /> Pengaturan</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={async () => { await logout(); nav("/login"); }} className="text-red-500" data-testid="profile-menu-logout"><LogOut className="mr-2 h-4 w-4" /> Keluar</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
